"use client";
import React, { useCallback, useEffect, useState } from "react";
import T from "@marginly/ui/components/typography";
import cn from "classnames";
import {
  Modal,
  ModalBody,
  ModalCross,
  ModalHeading,
  ModalLayout,
  ModalOverflow,
} from "components/common/modal.styled";
import {
  RatingEpicItem,
  RatingItem,
  RatingNum,
  RatingTeamInfo,
  RatingTotals,
  YourRank,
} from "./leaderboard/styled";
import { formatNum } from "util/format";
import { formatPnl, formatScore } from "util/format-pnl";
import Space from "components/common/space";
import { CONTEST_INIT_BALANCE } from "constants/common";
import { ByUserWithPositionRow, LeaderboardRow } from "types/api";
import { isLeaderboardRowList } from "types/type-guards";

interface Props {
  onHide: () => void;
  userAddress: string | undefined;
  list: ByUserWithPositionRow[] | LeaderboardRow[] | undefined;
  renderImgPath: (value: number) => string;
  topRank: number;
}

export const IndividualRatingModal = ({
  onHide,
  userAddress,
  list,
  renderImgPath,
  topRank,
}: Props) => {
  const [isModalHeaderColored, setIsModalHeaderColored] = useState(false);
  const [initialScroll, setInitialScroll] = useState(0);

  const handleHide = useCallback(() => {
    onHide();
  }, []);

  useEffect(() => {
    let scrollarea = document.getElementById("scollarea");
    let firstCildren = scrollarea?.firstElementChild;
    if (firstCildren) {
      setInitialScroll(firstCildren.getBoundingClientRect().top);
    }
  }, []);

  const handleScroll = useCallback(
    (firstCildren) => {
      if (firstCildren.getBoundingClientRect().top <= initialScroll) {
        setIsModalHeaderColored(true);
      } else setIsModalHeaderColored(false);
    },
    [initialScroll],
  );

  useEffect(() => {
    let scrollarea = document.getElementById("scollarea");
    let firstCildren = scrollarea?.firstElementChild;
    if (firstCildren) {
      scrollarea?.addEventListener("scroll", () => {
        handleScroll(firstCildren);
      });
    }
  }, [handleScroll]);

  const renderList = () => {
    if (!list?.length) return null;
    if (isLeaderboardRowList(list)) {
      return list
        ?.slice(0, topRank)
        .map(({ score, userAddress: listUserAddress }, key) => {
          const placeNumber = key + 1;
          return (
            <RatingItem key={key}>
              <RatingNum className="referral">{key + 1}</RatingNum>
              <RatingTeamInfo>
                <div>
                  <span>
                    {".." +
                      listUserAddress.substring(listUserAddress.length - 4)}
                  </span>
                  {userAddress == listUserAddress && (
                    <YourRank>Your rank</YourRank>
                  )}
                </div>
              </RatingTeamInfo>

              <RatingEpicItem className="in-modal">
                {placeNumber ? (
                  <img src={renderImgPath(placeNumber)} alt="" />
                ) : (
                  <></>
                )}
              </RatingEpicItem>
              <RatingTotals pnl={score}>{formatScore(score)}%</RatingTotals>
            </RatingItem>
          );
        });
    }
    return list.slice(0, topRank).map((item, key) => {
      const pnl =
        item.balanceValue + item.positionsValue - CONTEST_INIT_BALANCE;
      const placeNumber = key + 1;
      return (
        <RatingItem key={key}>
          <RatingNum className="referral">{key + 1}</RatingNum>
          <RatingTeamInfo>
            <div>
              <span>
                {".." + item.userAddress.substring(item.userAddress.length - 4)}
              </span>
              {userAddress == item.userAddress && (
                <YourRank>Your rank</YourRank>
              )}
            </div>
          </RatingTeamInfo>

          <RatingEpicItem className="in-modal">
            {placeNumber ? (
              <img src={renderImgPath(placeNumber)} alt="" />
            ) : (
              <></>
            )}
          </RatingEpicItem>
          <RatingTotals pnl={pnl}>{formatPnl(pnl)}</RatingTotals>
        </RatingItem>
      );
    });
  };

  return (
    <Modal>
      <ModalLayout onClick={handleHide} />
      <ModalCross onClick={handleHide}>
        <img src="images/button-close.svg" alt="close" className="cross" />
        <img src="images/goback.svg" alt="close" className="arrow" />
      </ModalCross>

      <ModalBody className="sm high" id="scollarea">
        <ModalHeading
          className={isModalHeaderColored ? "colored padded" : "padded"}
        >
          <div>
            <T headerL>Top {topRank} traders</T>
            <Space height={4} heightMobile={4} />
            <T secondary body>
              From {formatNum(list?.length)} participants.
            </T>
          </div>
        </ModalHeading>

        <ModalOverflow
          className={cn("padded", "full-height", !list && "pending")}
        >
          {renderList()}
          <Space height={0} heightMobile={18} />
          <div className="pending-div" />
        </ModalOverflow>
      </ModalBody>
    </Modal>
  );
};
